<template>
    <div class="send-email-config">
        <div class="config-field">
            <label>{{ $t("taskModuleRules.senderFrom") }}</label>
            <a-select
                v-model="localConfig.from"
                show-search
                :placeholder="$t('taskModuleRules.selectSenderEmail')"
                style="width: 100%;"
                @change="emitChange"
            >
                <a-select-option
                    v-for="mail in senderMails"
                    :key="mail.mail"
                    :value="mail.mail"
                >
                    {{ mail.mail }} ({{ getMailLabel(mail) }})
                </a-select-option>
            </a-select>
        </div>

        <div class="config-field">
            <label>{{ $t("taskModuleRules.recipientTypeTo") }}</label>
            <a-radio-group v-model="localConfig.toType" @change="emitChange" button-style="solid">
                <a-radio-button value="static">{{ $t("taskModuleRules.staticEmail") }}</a-radio-button>
                <a-radio-button value="dynamic">{{ $t("taskModuleRules.dynamic") }}</a-radio-button>
            </a-radio-group>
        </div>

        <div v-if="localConfig.toType === 'static'" class="config-field">
            <label>{{ $t("taskModuleRules.recipientEmailAddresses") }}</label>
            <a-select
                v-model="localConfig.toStatic"
                mode="tags"
                :placeholder="$t('taskModuleRules.enterEmailAddresses')"
                style="width: 100%;"
                @change="emitChange"
            >
            </a-select>
            <small>{{ $t("taskModuleRules.youCanEnterEmailsSeparatedByComma") }}</small>
        </div>

        <div v-if="localConfig.toType === 'dynamic'" class="config-field">
            <label>{{ $t("taskModuleRules.dynamicRecipients") }}</label>
            <a-select
                v-model="localConfig.toDynamic"
                mode="multiple"
                :placeholder="$t('taskModuleRules.selectDynamicRecipient')"
                style="width: 100%;"
                @change="emitChange"
            >
                <a-select-option value="task_owner">{{ $t("taskModuleRules.taskOwner") }}</a-select-option>
                <a-select-option value="task_assignees">{{ $t("taskModuleRules.taskAssignees") }}</a-select-option>
                <a-select-option value="task_followers">{{ $t("taskModuleRules.taskFollowers") }}</a-select-option>
                <a-select-option value="task_collaborators">{{ $t("taskModuleRules.taskCollaborators") }}</a-select-option>
                <a-select-option value="task_relation_visitor">{{ $t("taskModuleRules.relatedCustomer") }}</a-select-option>
            </a-select>
        </div>

        <div class="config-field">
            <label>{{ $t("taskModuleRules.ccOptional") }}</label>
            <a-select
                v-model="localConfig.cc"
                mode="tags"
                :placeholder="$t('taskModuleRules.ccEmailAddresses')"
                style="width: 100%;"
                @change="emitChange"
            >
            </a-select>
        </div>

        <div class="config-field">
            <label>{{ $t("taskModuleRules.bccOptional") }}</label>
            <a-select
                v-model="localConfig.bcc"
                mode="tags"
                :placeholder="$t('taskModuleRules.bccEmailAddresses')"
                style="width: 100%;"
                @change="emitChange"
            >
            </a-select>
        </div>

        <div class="config-field">
            <label>{{ $t("taskModuleRules.subjectSubject") }}</label>
            <div class="input-with-variables">
                <a-input
                    v-model="localConfig.subject"
                    :placeholder="$t('taskModuleRules.emailSubjectWithDynamicVariables')"
                    @change="emitChange"
                />
                <a-button type="link" size="small" @click="showVariablesPicker = true">
                    <i class="material-icons">code</i>
                    {{ $t("taskModuleRules.variables") }}
                </a-button>
            </div>
        </div>

        <div class="config-field">
            <label>{{ $t("taskModuleRules.contentBody") }}</label>
            <div class="input-with-variables">
                <a-textarea
                    v-model="localConfig.body"
                    :placeholder="$t('taskModuleRules.emailContentWithHtmlAndVariables')"
                    :rows="6"
                    @change="emitChange"
                />
                <a-button type="link" size="small" @click="showVariablesPicker = true">
                    <i class="material-icons">code</i>
                    {{ $t("taskModuleRules.variables") }}
                </a-button>
            </div>
            <small>{{ $t("taskModuleRules.youCanUseHtmlTags") }}</small>
        </div>

        <DynamicVariablesPicker
            v-if="showVariablesPicker"
            :visible="showVariablesPicker"
            @close="showVariablesPicker = false"
            @select="insertVariable"
        />
    </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import DynamicVariablesPicker from "./DynamicVariablesPicker.vue";

export default {
    name: "SendEmailConfig",
    components: {
        DynamicVariablesPicker
    },
    props: {
        value: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            localConfig: {
                from: '',
                toType: 'dynamic',
                toStatic: [],
                toDynamic: [],
                cc: [],
                bcc: [],
                subject: '',
                body: ''
            },
            showVariablesPicker: false
        };
    },
    computed: {
        ...mapGetters({
            allowedMailAddresses: "mail/allowedMailAddresses"
        }),
        ...mapState({
            users: state => state.user.users.data || [],
            subjects: state => state.subject.subjects.data || []
        }),
        senderMails() {
            return this.allowedMailAddresses.filter(mail => 
                mail.isSenderMail ? mail.isSenderVerified : mail.isSender
            );
        }
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal) {
                    this.localConfig = JSON.parse(JSON.stringify(newVal));
                }
            },
            immediate: true,
            deep: true
        }
    },
    methods: {
        emitChange() {
            this.$emit("input", JSON.parse(JSON.stringify(this.localConfig)));
        },

        getMailLabel(mail) {
            if (mail.type === 'user') {
                const user = this.users.find(u => u._id === mail.user);
                return user ? user.name : 'Kullanıcı';
            }
            if (mail.type === 'department') {
                const dept = this.subjects.find(s => s._id === mail.department);
                return dept ? dept.languageContent[this.$i18n.locale].name : 'Departman';
            }
            return 'Mail';
        },

        insertVariable(variable) {
            // Şimdilik subject'e ekle - geliştirilecek
            this.localConfig.subject += ` {{${variable}}}`;
            this.emitChange();
        }
    }
};
</script>

<style lang="scss" scoped>
.send-email-config {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .config-field {
        label {
            display: block;
            font-weight: 600;
            margin-bottom: 8px;
            color: #1a1a1a;
        }

        small {
            display: block;
            color: #999;
            font-size: 12px;
            margin-top: 6px;
        }

        .input-with-variables {
            display: flex;
            gap: 8px;
            align-items: flex-start;

            .ant-input,
            .ant-textarea {
                flex: 1;
            }
        }
    }
}
</style>
